/**
 * Short Web Audio alerts — a message beep and a phone ring.
 *
 * Synthesised rather than shipped as audio files: two tones and an
 * envelope, no asset to load, no 404 if a build forgets to copy it.
 *
 * Browsers suspend an AudioContext created without a prior user
 * gesture, so every call here is best-effort and silent on failure.
 * The visual side of a notification must never depend on the sound
 * having played.
 */

const BEEP_FREQUENCY_HZ = 800;
const BEEP_GAIN = 0.15;
const BEEP_DURATION_S = 0.3;

// A two-tone warble, roughly North American ringback.
const RING_HIGH_HZ = 480;
const RING_LOW_HZ = 440;
const RING_GAIN = 0.12;
const RING_ON_DURATION_S = 1.6;
const RING_INTERVAL_MS = 3400;

let audioContext: AudioContext | null = null;

const getContext = (): AudioContext | null => {
    try {
        if (!audioContext) audioContext = new AudioContext();
        // Created before any user gesture, a context starts suspended.
        // Resuming is itself best-effort.
        if (audioContext.state === 'suspended') void audioContext.resume();
        return audioContext;
    } catch {
        return null;
    }
};

/** One short beep — a message arriving while you're looking elsewhere. */
export const playNotificationBeep = (): void => {
    const ctx = getContext();
    if (!ctx) return;
    try {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.frequency.value = BEEP_FREQUENCY_HZ;
        gain.gain.value = BEEP_GAIN;
        oscillator.start();
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + BEEP_DURATION_S);
        oscillator.stop(ctx.currentTime + BEEP_DURATION_S);
    } catch {
        /* an inaudible notification is still a notification */
    }
};

const ringOnce = (ctx: AudioContext): void => {
    try {
        const gain = ctx.createGain();
        gain.connect(ctx.destination);
        gain.gain.value = RING_GAIN;
        const stopAt = ctx.currentTime + RING_ON_DURATION_S;
        for (const frequency of [RING_HIGH_HZ, RING_LOW_HZ]) {
            const oscillator = ctx.createOscillator();
            oscillator.frequency.value = frequency;
            oscillator.connect(gain);
            oscillator.start();
            oscillator.stop(stopAt);
        }
        gain.gain.setValueAtTime(RING_GAIN, stopAt - 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, stopAt);
    } catch {
        /* ignore */
    }
};

/**
 * Ring until the returned function is called. Always call it — on
 * accept, on decline, and on unmount — or the ring outlives the call.
 */
export const startRinging = (): (() => void) => {
    const ctx = getContext();
    if (!ctx) return () => { /* nothing started */ };
    ringOnce(ctx);
    const timer = window.setInterval(() => ringOnce(ctx), RING_INTERVAL_MS);
    return () => window.clearInterval(timer);
};
